import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { CategoryInput, Category, Funnel } from '../types';

import { apiService } from '../services/api';

const categorySchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  cor: z.string().min(1, 'Cor é obrigatória'),
  descricao: z.string().optional(),
  prompt: z.string().optional(),
  agentMainObjective: z.string().optional(),
  exemplosMensagens: z.string().optional(),
  visibleToLabeler: z.boolean().optional(),
  funnelId: z.preprocess((value) => (value ? String(value) : undefined), z.string().optional()),
  funnelOrder: z.preprocess(
    (value) => {
      if (value === '' || value === undefined || value === null) return undefined;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? undefined : parsed;
    },
    z.number().optional()
  ),
});

type CategoryFormData = z.infer<typeof categorySchema>;

interface CategoryFormProps {
  category?: Category;
  funnels?: Funnel[];
  onSuccess: () => void;
  onCancel: () => void;
  variant?: 'modal' | 'page';
  defaultFunnelId?: string;
}

const defaultColors = [
  '#1f2937', // Slate
  '#0f172a', // Navy
  '#2563eb', // Soft Blue
  '#38bdf8', // Sky
  '#22c55e', // Emerald
  '#fbbf24', // Amber
  '#fb7185', // Rose
  '#a855f7', // Violet
];

type ExpandedField = 'descricao' | 'prompt' | 'agentMainObjective' | null;

export function CategoryForm({
  category,
  funnels = [],
  onSuccess,
  onCancel,
  variant = 'modal',
  defaultFunnelId,
}: CategoryFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      nome: category?.nome || '',
      cor: category?.cor || '#1f2937',
      descricao: category?.descricao || '',
      prompt: category?.prompt || '',
      agentMainObjective: category?.agentMainObjective || '',
      exemplosMensagens: category?.exemplosMensagens || '',
      visibleToLabeler: category?.visibleToLabeler ?? true,
      funnelId: category?.funnelId || defaultFunnelId || '',
      funnelOrder: category?.funnelOrder ?? undefined,
    },
  });

  const selectedColor = watch('cor');
  const isVisibleToLabeler = watch('visibleToLabeler') !== false;
  const descriptionValue = watch('descricao');
  const promptValue = watch('prompt');
  const selectedFunnelId = watch('funnelId');
  const selectedFunnel = funnels.find((funnel) => funnel.id === selectedFunnelId);
  const hasFunnel = Boolean(selectedFunnel);
  const [expandedField, setExpandedField] = useState<ExpandedField>(null);
  const [aiField, setAiField] = useState<'descricao' | 'prompt' | null>(null);
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const handleAiRewrite = async () => {
    if (!aiField || !aiInstruction.trim()) return;
    setAiLoading(true);
    try {
      const currentText = aiField === 'descricao' ? (descriptionValue || '') : (promptValue || '');
      const result = await apiService.aiRewriteCategoryField({
        currentText,
        instruction: aiInstruction.trim(),
        field: aiField,
      });
      setValue(aiField, result.rewrittenText);
      toast.success('Texto reescrito com sucesso pela IA');
      setAiField(null);
      setAiInstruction('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao reescrever com IA');
    } finally {
      setAiLoading(false);
    }
  };

  const onSubmit = async (data: CategoryFormData) => {
    try {
      const funnelId = data.funnelId ? data.funnelId : null;
      const usesBase = Boolean(funnelId && selectedFunnel);
      // Salva a categoria. Em funil com base, mandamos objetivo/exemplos (independentes
      //    por etapa) e o backend remonta o prompt final. Sem funil, mantém o prompt cru.
      const categoryInput: CategoryInput = {
        nome: data.nome,
        cor: data.cor,
        descricao: data.descricao || undefined,
        visibleToLabeler: data.visibleToLabeler ?? true,
        funnelId,
        funnelOrder: data.funnelOrder ?? null,
        ...(usesBase
          ? {
              agentMainObjective: data.agentMainObjective || undefined,
              exemplosMensagens: data.exemplosMensagens || '',
            }
          : { prompt: data.prompt || undefined }),
      };

      if (category) {
        await apiService.updateCategory(category.id, categoryInput);
        toast.success('Categoria atualizada com sucesso');
      } else {
        await apiService.createCategory(categoryInput);
        toast.success('Categoria criada com sucesso');
      }

      onSuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar categoria';
      toast.error(errorMessage);
    }
  };

  const content = (
    <div
      className={
        variant === 'modal'
          ? 'bg-white rounded-[28px] shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-y-auto border border-slate-200'
          : 'w-full'
      }
      role="dialog"
      aria-labelledby="form-title"
    >
      <div className={variant === 'modal' ? 'p-6 md:p-8' : ''}>
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-gradient-to-r from-slate-950 via-slate-900 to-blue-900 px-6 py-6 md:px-8">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-2xl">
                <span className="inline-flex w-fit items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-100">
                  {category ? 'Edição de categoria' : 'Nova categoria'}
                </span>
                <h2 id="form-title" className="mt-4 text-2xl font-bold text-white md:text-3xl">
                  {category ? 'Refine a categoria com mais clareza' : 'Monte uma nova etapa do seu funil'}
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-300 md:text-base">
                  {category
                    ? 'Ajuste nome, vínculo no funil, descrição e prompt em uma tela mais organizada para editar com segurança.'
                    : 'Defina a identidade visual, posicione a categoria no funil e escreva o conteúdo que guiará o agente.'}
                </p>
              </div>

              <div className="min-w-[280px] rounded-3xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                <div className="flex items-start gap-3">
                  <div className="mt-1 h-4 w-4 rounded-full border border-white/40" style={{ backgroundColor: selectedColor }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-100">Preview</p>
                    <p className="mt-2 truncate text-lg font-semibold text-white">
                      {watch('nome') || 'Nome da categoria'}
                    </p>
                    <p className="mt-1 text-sm text-slate-300">
                      {selectedFunnel?.nome || 'Sem funil vinculado'}
                    </p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-slate-200">
                  <div className="rounded-2xl border border-white/10 bg-black/10 px-3 py-2">
                    <div className="text-slate-400">Etiquetador</div>
                    <div className="mt-1 font-semibold text-white">{isVisibleToLabeler ? 'Visível' : 'Oculta'}</div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-black/10 px-3 py-2">
                    <div className="text-slate-400">Ordem</div>
                    <div className="mt-1 font-semibold text-white">
                      {watch('funnelOrder') === undefined || watch('funnelOrder') === null || Number.isNaN(watch('funnelOrder'))
                        ? 'Não definida'
                        : watch('funnelOrder')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 md:p-8">
            <div className="grid gap-6 xl:grid-cols-[1.05fr_1.35fr]">
              <div className="space-y-6">
                <section className="rounded-3xl border border-slate-200 bg-slate-50/70 p-5 md:p-6">
                  <div className="mb-5">
                    <h3 className="text-base font-semibold text-slate-900">Identificação</h3>
                    <p className="mt-1 text-sm text-slate-500">Defina como essa categoria aparece visualmente para o usuário.</p>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <label htmlFor="nome" className="mb-2 block text-sm font-semibold text-slate-700">
                        Nome *
                      </label>
                      <input
                        id="nome"
                        type="text"
                        {...register('nome')}
                        className="input-field rounded-2xl border-slate-200 bg-white"
                        placeholder="Ex: Qualificação, Fechamento, Pós-venda"
                        aria-invalid={errors.nome ? 'true' : 'false'}
                        aria-describedby={errors.nome ? 'nome-error' : undefined}
                      />
                      {errors.nome && (
                        <p id="nome-error" className="mt-1 text-sm text-red-500">
                          {errors.nome.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <label htmlFor="cor" className="block text-sm font-semibold text-slate-700">
                          Cor *
                        </label>
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-500">
                          {selectedColor}
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-3 sm:grid-cols-8">
                        {defaultColors.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setValue('cor', color)}
                            className={`h-12 rounded-2xl border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                              selectedColor === color
                                ? 'scale-[1.03] border-slate-900 ring-2 ring-slate-300'
                                : 'border-white hover:border-slate-300'
                            }`}
                            style={{ backgroundColor: color }}
                            aria-label={`Selecionar cor ${color}`}
                          />
                        ))}
                      </div>

                      <div className="mt-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                        <input
                          id="cor"
                          type="color"
                          {...register('cor')}
                          className="h-12 w-16 cursor-pointer rounded-xl border border-slate-300"
                          aria-invalid={errors.cor ? 'true' : 'false'}
                          aria-describedby={errors.cor ? 'cor-error' : undefined}
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-700">Cor personalizada</div>
                          <div className="text-xs text-slate-500">Escolha qualquer tom para diferenciar esta etapa no funil.</div>
                        </div>
                      </div>
                      {errors.cor && (
                        <p id="cor-error" className="mt-1 text-sm text-red-500">
                          {errors.cor.message}
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                <section className="rounded-3xl border border-slate-200 bg-white p-5 md:p-6">
                  <div className="mb-5">
                    <h3 className="text-base font-semibold text-slate-900">Vínculo com o funil</h3>
                    <p className="mt-1 text-sm text-slate-500">Escolha em qual funil essa categoria ficará e em que posição ela deve aparecer.</p>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <label htmlFor="funnelId" className="mb-2 block text-sm font-semibold text-slate-700">
                        Funil
                      </label>
                      <select
                        id="funnelId"
                        {...register('funnelId')}
                        className="input-field rounded-2xl border-slate-200 bg-white"
                      >
                        <option value="">Sem funil</option>
                        {funnels.map((funnel) => (
                          <option key={funnel.id} value={funnel.id}>
                            {funnel.nome}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="funnelOrder" className="mb-2 block text-sm font-semibold text-slate-700">
                        Ordem no funil
                      </label>
                      <input
                        id="funnelOrder"
                        type="number"
                        min={0}
                        {...register('funnelOrder', { valueAsNumber: true })}
                        className="input-field rounded-2xl border-slate-200 bg-white"
                        placeholder="0"
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        Menor número aparece primeiro. Use isso para organizar a sequência das etapas.
                      </p>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <label className="mb-2 block text-sm font-semibold text-slate-700">
                        Visível p/ etiquetador
                      </label>
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-700">
                            {isVisibleToLabeler ? 'Categoria visível' : 'Categoria oculta'}
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            Controla se essa etapa aparece para o agente etiquetador durante a classificação.
                          </p>
                        </div>

                        <label className="relative inline-flex items-center cursor-pointer select-none">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            {...register('visibleToLabeler')}
                          />
                          <button
                            type="button"
                            onClick={() => setValue('visibleToLabeler', !isVisibleToLabeler)}
                            className={`relative h-7 w-12 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                              isVisibleToLabeler ? 'bg-emerald-600 focus:ring-emerald-500' : 'bg-slate-300 focus:ring-slate-400'
                            }`}
                            aria-label={
                              isVisibleToLabeler
                                ? 'Ocultar categoria do etiquetador'
                                : 'Tornar categoria visível para o etiquetador'
                            }
                          >
                            <span
                              className={`absolute top-[2px] left-[2px] h-6 w-6 rounded-full border border-slate-200 bg-white transition-transform duration-200 ${
                                isVisibleToLabeler ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </label>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              <div className="space-y-6">
                <section className="rounded-3xl border border-slate-200 bg-white p-5 md:p-6">
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Descrição</h3>
                      <p className="mt-1 text-sm text-slate-500">Explique rapidamente o papel dessa categoria no seu processo.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => { setAiField('descricao'); setAiInstruction(''); }}
                        className="rounded-full border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-100"
                      >
                        🤖 Editar com IA
                      </button>
                      <button
                        type="button"
                        onClick={() => setExpandedField('descricao')}
                        className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
                        aria-label="Expandir descrição"
                      >
                        Expandir
                      </button>
                    </div>
                  </div>
                  <textarea
                    id="descricao"
                    {...register('descricao')}
                    rows={8}
                    className="input-field min-h-[190px] resize-y rounded-2xl border-slate-200 bg-slate-50/50"
                    placeholder="Descreva a função desta categoria, quando ela deve ser usada e o que a diferencia das demais."
                  />
                </section>

                {hasFunnel ? (
                  <>
                    {/* Objetivo desta etapa — INDEPENDENTE por etapa */}
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 md:p-6">
                      <div className="mb-3">
                        <h3 className="text-base font-semibold text-slate-900">Objetivo desta etapa</h3>
                        <p className="mt-1 text-sm text-slate-500">
                          Independente por etapa — é a única coisa que muda de uma categoria para outra.
                        </p>
                      </div>
                      <textarea
                        id="agentMainObjective"
                        {...register('agentMainObjective')}
                        rows={4}
                        className="input-field min-h-[110px] resize-y rounded-2xl border-slate-200 bg-slate-50/50"
                        placeholder="Ex: Fazer o lead aceitar ouvir sobre o produto (levar à próxima etapa)."
                      />
                    </section>

                    {/* Exemplos desta etapa — INDEPENDENTE por etapa */}
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 md:p-6">
                      <div className="mb-3">
                        <h3 className="text-base font-semibold text-slate-900">Exemplos de mensagens</h3>
                        <p className="mt-1 text-sm text-slate-500">
                          Independente por etapa. 3-4 mensagens curtas de WhatsApp para esta fase.
                        </p>
                      </div>
                      <textarea
                        id="exemplosMensagens"
                        {...register('exemplosMensagens')}
                        rows={6}
                        className="input-field min-h-[140px] resize-y rounded-2xl border-slate-200 bg-slate-50/50 font-mono text-sm"
                        placeholder={'- Oi! Sou o Marcos da SolarTech...\n- Show! Vc paga mais de 2mil de luz?\n- Posso te apresentar rapidinho?'}
                      />
                    </section>

                    {/* Prompt final — somente leitura (montado da base + objetivo + exemplos) */}
                    <section className="rounded-3xl border border-slate-200 bg-slate-50/70 p-5 md:p-6">
                      <div className="mb-3">
                        <h3 className="text-base font-semibold text-slate-900">Prompt final (gerado)</h3>
                        <p className="mt-1 text-sm text-slate-500">
                          Montado automaticamente: base do funil + objetivo + exemplos desta etapa.
                        </p>
                      </div>
                      <pre className="max-h-[280px] overflow-y-auto whitespace-pre-wrap break-words rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-600">
                        {category?.prompt?.trim() || 'Será gerado ao salvar.'}
                      </pre>
                    </section>
                  </>
                ) : (
                  <>
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 md:p-6">
                      <div className="mb-5 flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-base font-semibold text-slate-900">Prompt da categoria</h3>
                          <p className="mt-1 text-sm text-slate-500">Sem funil vinculado, o prompt é livre. Vincule a um funil para separar personalidade e objetivo.</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => { setAiField('prompt'); setAiInstruction(''); }}
                            className="rounded-full border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-100"
                          >
                            🤖 Editar com IA
                          </button>
                          <button
                            type="button"
                            onClick={() => setExpandedField('prompt')}
                            className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
                            aria-label="Expandir prompt"
                          >
                            Expandir
                          </button>
                        </div>
                      </div>
                      <textarea
                        id="prompt"
                        {...register('prompt')}
                        rows={12}
                        className="input-field min-h-[280px] resize-y rounded-2xl border-slate-200 bg-slate-50/50 font-mono text-sm"
                        placeholder="Escreva aqui as instruções específicas que essa categoria deve carregar."
                      />
                      <p className="mt-2 text-xs text-slate-500">
                        Esse prompt ficará salvo junto à categoria e será exclusivo para o seu tenant.
                      </p>
                    </section>

                    <section className="rounded-3xl border border-slate-200 bg-slate-50/70 p-5 md:p-6">
                      <div className="mb-4 flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-base font-semibold text-slate-900">Objetivo principal</h3>
                          <p className="mt-1 text-sm text-slate-500">Campo gerado automaticamente com base no prompt salvo.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setExpandedField('agentMainObjective')}
                          className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
                          aria-label="Expandir objetivo principal"
                        >
                          Expandir
                        </button>
                      </div>
                      <textarea
                        id="agentMainObjective"
                        value={category?.agentMainObjective || ''}
                        readOnly
                        rows={5}
                        className="input-field resize-none rounded-2xl border-slate-200 bg-white/80"
                        placeholder="Será gerado automaticamente ao salvar o prompt"
                      />
                    </section>
                  </>
                )}
              </div>
            </div>

            <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-200 pt-6 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                aria-label="Cancelar operação"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={category ? 'Salvar alterações da categoria' : 'Criar nova categoria'}
              >
                {isSubmitting ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Gerando objetivo e salvando...
                  </>
                ) : (
                  'Salvar categoria'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const aiRewriteModal = aiField && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
        <div className="border-b border-slate-200 bg-gradient-to-r from-slate-950 via-slate-900 to-purple-900 px-6 py-6 md:px-8">
          <div className="flex items-start justify-between gap-4">
            <div className="max-w-2xl">
              <span className="inline-flex items-center rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-100">
                Assistente de escrita
              </span>
              <h3 className="mt-4 text-2xl font-bold text-white md:text-3xl">
                Editar {aiField === 'descricao' ? 'descrição' : 'prompt'} com IA
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-300 md:text-base">
                Descreva a melhoria desejada e eu reescrevo o conteúdo mantendo o contexto da categoria.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setAiField(null);
                setAiInstruction('');
              }}
              className="rounded-2xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
              disabled={aiLoading}
            >
              Fechar
            </button>
          </div>
        </div>

        <div className="grid gap-6 p-6 md:grid-cols-[1fr_1.15fr] md:p-8">
          <section className="rounded-3xl border border-slate-200 bg-slate-50/70 p-5 md:p-6">
            <div className="mb-4">
              <h4 className="text-base font-semibold text-slate-900">Texto atual</h4>
              <p className="mt-1 text-sm text-slate-500">Revise o conteúdo antes de pedir a alteração.</p>
            </div>
            <div className="min-h-[360px] max-h-[58vh] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 text-sm leading-6 whitespace-pre-wrap text-slate-700">
              {(aiField === 'descricao' ? descriptionValue : promptValue) || '(vazio)'}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 md:p-6">
            <div className="mb-4">
              <h4 className="text-base font-semibold text-slate-900">Instrução para a IA</h4>
              <p className="mt-1 text-sm text-slate-500">Explique o tom, objetivo ou pontos que devem ser reforçados.</p>
            </div>

            <textarea
              value={aiInstruction}
              onChange={(e) => setAiInstruction(e.target.value)}
              rows={12}
              className="input-field min-h-[360px] resize-y rounded-2xl border-slate-200 bg-slate-50/50"
              placeholder="Ex: deixe mais objetivo, destaque objeções comuns, adote um tom mais direto e comercial..."
              disabled={aiLoading}
            />

            <div className="mt-6 flex flex-col-reverse gap-3 border-t border-slate-200 pt-6 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setAiField(null);
                  setAiInstruction('');
                }}
                className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                disabled={aiLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleAiRewrite}
                disabled={aiLoading || !aiInstruction.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-purple-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {aiLoading ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Reescrevendo...
                  </>
                ) : (
                  '🤖 Reescrever com IA'
                )}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );

  const expandedValue =
    expandedField === 'descricao'
      ? descriptionValue || ''
      : expandedField === 'prompt'
        ? promptValue || ''
        : category?.agentMainObjective || '';
  const expandedLabel =
    expandedField === 'descricao'
      ? 'Descrição'
      : expandedField === 'prompt'
        ? 'Prompt'
        : 'Objetivo principal';
  const expandedReadOnly = expandedField === 'agentMainObjective';

  const expandedModal = expandedField && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
        <div className="border-b border-slate-200 bg-gradient-to-r from-slate-950 via-slate-900 to-blue-900 px-6 py-6 md:px-8">
          <div className="flex items-start justify-between gap-4">
            <div className="max-w-2xl">
              <span className="inline-flex items-center rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-100">
                Edição ampliada
              </span>
              <h3 className="mt-4 text-2xl font-bold text-white md:text-3xl">{expandedLabel}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-300 md:text-base">
                {expandedReadOnly ? 'Visualize o conteúdo gerado automaticamente.' : 'Edite com mais espaço e feche quando terminar.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setExpandedField(null)}
              className="rounded-2xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
            >
              Fechar
            </button>
          </div>
        </div>

        <div className="p-6 md:p-8">
          <textarea
            value={expandedValue}
            readOnly={expandedReadOnly}
            onChange={(event) => {
              if (expandedField === 'descricao') {
                setValue('descricao', event.target.value);
              }
              if (expandedField === 'prompt') {
                setValue('prompt', event.target.value);
              }
            }}
            rows={20}
            className={`input-field min-h-[65vh] resize-none rounded-3xl border-slate-200 ${expandedReadOnly ? 'bg-slate-50' : 'bg-white'}`}
            placeholder=""
          />
          {!expandedReadOnly && (
            <p className="mt-3 text-sm text-slate-500">Edite o conteúdo livremente e clique em fechar para voltar ao formulário.</p>
          )}
        </div>
      </div>
    </div>
  );

  if (variant === 'page') {
    return (
      <div className="p-6">
        {content}
        {aiRewriteModal}
        {expandedModal}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4 backdrop-blur-sm">
      {content}
      {aiRewriteModal}
      {expandedModal}
    </div>
  );
}
